import { useState, useEffect } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  FolderKanban,
  Wallet,
  User,
  Settings,
  Menu,
  X,
  ChevronDown,
  LogOut,
  Bell,
  HelpCircle,
  Users,
  Workflow,
  LayoutDashboardIcon,
  File,
  Ticket,
  ExternalLink,
  Headphones,
} from "lucide-react";
import logo from "../assets/logo.png";
import { useAuth } from "../context/AuthContext";
import { getDecodedJwt } from "../lib/auth";
import { useProfileCompletion } from "../lib/utils/useProfileCompletion";

interface MenuItem {
  name: string;
  icon: React.ElementType;
  path: string;
  badge?: string | number;
  external?: boolean;
}

const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [user, setUser] = useState(getDecodedJwt());

  // Listen for user updates
  useEffect(() => {
    const handleUserUpdate = () => {
      setUser(getDecodedJwt());
    };

    window.addEventListener("userUpdated", handleUserUpdate);

    return () => {
      window.removeEventListener("userUpdated", handleUserUpdate);
    };
  }, []);

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".profile-dropdown")) {
        setProfileDropdownOpen(false);
      }
      if (!target.closest(".notifications-dropdown")) {
        setNotificationsOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const notifications = [
    {
      id: 1,
      title: "New task available",
      message: "AI Training batch #234 is ready",
      time: "2 min ago",
      unread: true,
    },
    {
      id: 2,
      title: "Payment received",
      message: "$125.00 has been deposited",
      time: "1 hour ago",
      unread: true,
    },
    {
      id: 3,
      title: "Certification expiring",
      message: "Renew your Data Annotation cert",
      time: "2 hours ago",
      unread: false,
    },
  ];

  const unreadCount = notifications.filter((n) => n.unread).length;

  const { percentage } = useProfileCompletion();

  const isAdmin = user?.role === "admin";
  const isContributor = user?.role === "contributor";
  const showProfileCompletion = isContributor && percentage < 100;

  const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL;
  const helpUrl = `${FRONTEND_URL}/help`;

  // Main navigation items
  const menuItems: MenuItem[] = isContributor
    ? [
        { name: "Dashboard", icon: LayoutDashboard, path: "/" },
        { name: "Projects", icon: FolderKanban, path: "/projects" },
        { name: "Payments", icon: Wallet, path: "/payments" },
        { name: "Profile", icon: User, path: "/profile" },
        { name: "Settings", icon: Settings, path: "/settings" },
      ]
    : [
        { name: "Dashboard", icon: LayoutDashboardIcon, path: "/admin/home" },
        { name: "Applications", icon: File, path: "/admin/applications" },
        { name: "Users", icon: Users, path: "/admin/users" },
        { name: "Projects", icon: Workflow, path: "/admin/projects" },
        { name: "Tickets", icon: Ticket, path: "/admin/tickets" },
      ];

  // Support/Help item (separate from main menu)
  const supportItem: MenuItem = isAdmin
    ? { name: "Support Tickets", icon: Headphones, path: "/admin/tickets" }
    : {
        name: "Help & Support",
        icon: HelpCircle,
        path: helpUrl,
        external: true,
      };

  // Handle support link click
  const handleSupportClick = (e: React.MouseEvent) => {
    if (supportItem.external) {
      e.preventDefault();
      window.open(supportItem.path, "_blank");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navbar */}
      <header className="fixed top-0 left-0 right-0 h-16 bg-white border-b border-gray-200 z-[9995] transform-gpu">
        <div className="flex items-center justify-between h-full px-4">
          {/* Left Section */}
          <div className="flex items-center gap-4">
            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <Menu className="w-6 h-6 text-gray-600" />
            </button>

            {/* Desktop Sidebar Toggle */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="hidden lg:flex p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <Menu className="w-6 h-6 text-gray-600" />
            </button>

            {/* Logo */}
            <NavLink to="/" className="flex items-center gap-2">
              <img src={logo} alt="Unimerg" className="h-8 w-auto" />
            </NavLink>
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Help Button */}
            <button
              onClick={() => window.open(helpUrl, "_blank")}
              className="hidden sm:flex p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <HelpCircle className="w-5 h-5 text-gray-500" />
            </button>

            {/* Notifications */}
            <div className="relative notifications-dropdown">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setNotificationsOpen(!notificationsOpen);
                }}
                className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors hidden"
              >
                <Bell className="w-5 h-5 text-gray-500" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-[#E05A6A] text-white text-xs flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </button>

              {/* Notifications Dropdown */}
              <AnimatePresence>
                {notificationsOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden"
                  >
                    <div className="p-4 border-b border-gray-100">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-gray-900">
                          Notifications
                        </h3>
                        <button className="text-sm text-[#8B52FF] hover:underline">
                          Mark all read
                        </button>
                      </div>
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {notifications.map((notification) => (
                        <div
                          key={notification.id}
                          className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                            notification.unread ? "bg-[#8B52FF]/5" : ""
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            {notification.unread && (
                              <div className="w-2 h-2 rounded-full bg-[#8B52FF] mt-2 flex-shrink-0" />
                            )}
                            <div className={notification.unread ? "" : "ml-5"}>
                              <p className="font-medium text-gray-900 text-sm">
                                {notification.title}
                              </p>
                              <p className="text-gray-500 text-sm mt-0.5">
                                {notification.message}
                              </p>
                              <p className="text-gray-400 text-xs mt-1">
                                {notification.time}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="p-3 border-t border-gray-100">
                      <button className="w-full py-2 text-sm text-[#8B52FF] hover:bg-[#8B52FF]/5 rounded-lg transition-colors">
                        View all notifications
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Profile Dropdown */}
            <div className="relative profile-dropdown">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setProfileDropdownOpen(!profileDropdownOpen);
                }}
                className="flex items-center gap-3 p-1.5 pr-3 rounded-xl hover:bg-gray-100 transition-colors"
              >
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#8B52FF] to-[#E05A6A] flex items-center justify-center text-white font-semibold">
                  {`${user?.firstname[0]}${user?.lastname[0]}`}
                </div>
                <div className="hidden sm:block text-left">
                  <p className="text-sm font-medium text-gray-900">{`${user?.firstname} ${user?.lastname}`}</p>
                  <p className="text-xs text-gray-500 capitalize">
                    {`${user?.role}`}
                  </p>
                </div>
                <ChevronDown
                  className={`hidden sm:block w-4 h-4 text-gray-400 transition-transform ${
                    profileDropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {/* Profile Dropdown Menu */}
              <AnimatePresence>
                {profileDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden"
                  >
                    <div className="p-4 border-b border-gray-100">
                      <p className="font-medium text-gray-900">{`${user?.firstname} ${user?.lastname}`}</p>
                      <p className="text-sm text-gray-500">{`${user?.email}`}</p>
                    </div>
                    <div className="p-2">
                      <NavLink
                        to="/profile"
                        className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <User className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-700">
                          View Profile
                        </span>
                      </NavLink>
                      <NavLink
                        to="/settings"
                        className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <Settings className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-700">Settings</span>
                      </NavLink>
                    </div>
                    <div className="p-2 border-t border-gray-100">
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-red-50 transition-colors w-full text-left"
                      >
                        <LogOut className="w-4 h-4 text-red-500" />
                        <span className="text-sm text-red-600">Logout</span>
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </header>

      {/* Desktop Sidebar */}
      <aside
        className={`fixed left-0 top-16 h-[calc(100vh-4rem)] bg-white border-r border-gray-200 hidden lg:flex flex-col transition-all duration-300 z-30 ${
          sidebarOpen ? "w-64" : "w-20"
        }`}
      >
        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          {menuItems.map((item) => (
            <NavLink
              key={item.name}
              to={item.path}
              end={item.path === "/" || item.path === "/admin/home"}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-3 rounded-xl transition-all group relative ${
                  isActive
                    ? "bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white shadow-lg shadow-[#8B52FF]/25"
                    : "text-gray-600 hover:bg-gray-100"
                }`
              }
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && (
                <>
                  <span className="font-medium">{item.name}</span>
                  {item.badge && (
                    <span className="ml-auto px-2 py-0.5 rounded-full text-xs font-semibold bg-white/20">
                      {item.badge}
                    </span>
                  )}
                </>
              )}
              {!sidebarOpen && item.badge && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#E05A6A] text-white text-xs flex items-center justify-center">
                  {item.badge}
                </span>
              )}
              {/* Tooltip for collapsed sidebar */}
              {!sidebarOpen && (
                <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-50">
                  {item.name}
                  {item.badge && (
                    <span className="ml-2 px-1.5 py-0.5 rounded bg-[#E05A6A] text-xs">
                      {item.badge}
                    </span>
                  )}
                </div>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Sidebar Footer - Support & Profile Completion */}
        <div className="p-4 border-t border-gray-100 space-y-4">
          {/* Support/Help Link */}
          {isContributor ? (
            // External link for contributors
            <a
              href={supportItem.path}
              onClick={handleSupportClick}
              className={`flex items-center gap-3 px-3 py-3 rounded-xl transition-all group relative text-gray-600 hover:bg-gradient-to-r hover:from-[#8B52FF]/10 hover:to-[#E05A6A]/10 hover:text-[#8B52FF]`}
            >
              <supportItem.icon className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && (
                <>
                  <span className="font-medium">{supportItem.name}</span>
                  <ExternalLink className="w-4 h-4 ml-auto opacity-50" />
                </>
              )}
              {/* Tooltip for collapsed sidebar */}
              {!sidebarOpen && (
                <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-50 flex items-center gap-2">
                  {supportItem.name}
                  <ExternalLink className="w-3 h-3" />
                </div>
              )}
            </a>
          ) : (
            // Internal NavLink for admins - navigates to tickets page
            <NavLink
              to="/admin/tickets"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-3 rounded-xl transition-all group relative ${
                  isActive
                    ? "bg-gradient-to-r from-[#8B52FF]/10 to-[#E05A6A]/10 text-[#8B52FF]"
                    : "text-gray-600 hover:bg-gradient-to-r hover:from-[#8B52FF]/10 hover:to-[#E05A6A]/10 hover:text-[#8B52FF]"
                }`
              }
            >
              <Headphones className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && (
                <span className="font-medium">Support Tickets</span>
              )}
              {/* Tooltip for collapsed sidebar */}
              {!sidebarOpen && (
                <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-50">
                  Support Tickets
                </div>
              )}
            </NavLink>
          )}

          {/* Profile Completion - Only show for contributors with < 100% */}
          {showProfileCompletion && sidebarOpen && (
            <div className="p-4 rounded-xl bg-gradient-to-br from-[#8B52FF]/10 to-[#E05A6A]/10">
              <div className="flex items-center gap-3 mb-3">
                <div>
                  <p className="font-semibold text-gray-900 text-xs">
                    Profile {percentage}% Complete
                  </p>
                  <p className="text-xs text-gray-500">
                    Complete to unlock all features
                  </p>
                </div>
              </div>
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] rounded-full transition-all duration-500"
                  style={{ width: `${percentage}%` }}
                />
              </div>
            </div>
          )}

          {/* Collapsed sidebar - Profile completion indicator */}
          {showProfileCompletion && !sidebarOpen && (
            <div className="relative group">
              <div className="w-12 h-12 mx-auto rounded-xl bg-gradient-to-br from-[#8B52FF]/10 to-[#E05A6A]/10 flex items-center justify-center">
                <div className="relative w-8 h-8">
                  <svg className="w-8 h-8 transform -rotate-90">
                    <circle
                      cx="16"
                      cy="16"
                      r="14"
                      stroke="#e5e7eb"
                      strokeWidth="3"
                      fill="none"
                    />
                    <circle
                      cx="16"
                      cy="16"
                      r="14"
                      stroke="url(#gradient)"
                      strokeWidth="3"
                      fill="none"
                      strokeDasharray={`${(percentage / 100) * 88} 88`}
                      strokeLinecap="round"
                    />
                    <defs>
                      <linearGradient
                        id="gradient"
                        x1="0%"
                        y1="0%"
                        x2="100%"
                        y2="0%"
                      >
                        <stop offset="0%" stopColor="#8B52FF" />
                        <stop offset="100%" stopColor="#E05A6A" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-gray-700">
                    {percentage}
                  </span>
                </div>
              </div>
              {/* Tooltip */}
              <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-50 top-1/2 -translate-y-1/2">
                Profile {percentage}% Complete
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {mobileSidebarOpen && (
          <>
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileSidebarOpen(false)}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
            />

            {/* Sidebar */}
            <motion.aside
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed left-0 top-0 h-full w-80 bg-white shadow-2xl z-50 lg:hidden flex flex-col"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <img src={logo} alt="Unimerg" className="h-8 w-auto" />
                </div>
                <button
                  onClick={() => setMobileSidebarOpen(false)}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* User Info */}
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#8B52FF] to-[#E05A6A] flex items-center justify-center text-white font-bold text-lg">
                    {`${user?.firstname[0]}${user?.lastname[0]}`}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{`${user?.firstname} ${user?.lastname}`}</p>
                    <p className="text-sm text-gray-500 capitalize">{`${user?.role}`}</p>
                  </div>
                </div>
              </div>

              {/* Navigation */}
              <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
                {menuItems.map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.path}
                    end={item.path === "/" || item.path === "/admin/home"}
                    onClick={() => setMobileSidebarOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                        isActive
                          ? "bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white shadow-lg shadow-[#8B52FF]/25"
                          : "text-gray-600 hover:bg-gray-100"
                      }`
                    }
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.name}</span>
                    {item.badge && (
                      <span
                        className={`ml-auto px-2 py-0.5 rounded-full text-xs font-semibold ${
                          location.pathname === item.path
                            ? "bg-white/20"
                            : "bg-[#8B52FF]/10 text-[#8B52FF]"
                        }`}
                      >
                        {item.badge}
                      </span>
                    )}
                  </NavLink>
                ))}
              </nav>

              {/* Footer */}
              <div className="p-4 border-t border-gray-100 space-y-4">
                {/* Support/Help Link */}
                {isContributor ? (
                  <a
                    href={supportItem.path}
                    onClick={(e) => {
                      handleSupportClick(e);
                      setMobileSidebarOpen(false);
                    }}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-600 hover:bg-gradient-to-r hover:from-[#8B52FF]/10 hover:to-[#E05A6A]/10 hover:text-[#8B52FF] transition-all"
                  >
                    <HelpCircle className="w-5 h-5" />
                    <span className="font-medium">{supportItem.name}</span>
                    <ExternalLink className="w-4 h-4 ml-auto opacity-50" />
                  </a>
                ) : (
                  <NavLink
                    to="/admin/tickets"
                    onClick={() => setMobileSidebarOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                        isActive
                          ? "bg-gradient-to-r from-[#8B52FF]/10 to-[#E05A6A]/10 text-[#8B52FF]"
                          : "text-gray-600 hover:bg-gradient-to-r hover:from-[#8B52FF]/10 hover:to-[#E05A6A]/10 hover:text-[#8B52FF]"
                      }`
                    }
                  >
                    <Headphones className="w-5 h-5" />
                    <span className="font-medium">Support Tickets</span>
                  </NavLink>
                )}

                {/* Profile Completion - Only show for contributors with < 100% */}
                {showProfileCompletion && (
                  <div className="p-4 rounded-xl bg-gradient-to-br from-[#8B52FF]/10 to-[#E05A6A]/10">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#8B52FF] to-[#E05A6A] flex items-center justify-center">
                        <User className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">
                          Profile {percentage}% Complete
                        </p>
                        <p className="text-xs text-gray-500">
                          Complete to unlock all features
                        </p>
                      </div>
                    </div>
                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                )}

                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-red-50 transition-colors w-full text-left"
                >
                  <LogOut className="w-5 h-5 text-red-500" />
                  <span className="font-medium text-red-600">Logout</span>
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main
        className={`pt-16 min-h-screen transition-all duration-300 ${
          sidebarOpen ? "lg:pl-64" : "lg:pl-20"
        }`}
      >
        <div className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
